import { createSlice } from "@reduxjs/toolkit";

import { get, patch, post } from "../../helpers/axios";
import {
  GET_DOMAINS_LIST,
  GET_EMPLOYEE_LIST,
  GET_EMPLOYEE_LIST_FOR_PLATFORM_ADMIN,
  PATCH_ACTIVATE_EMPLOYEE,
  PATCH_DELETE_EMPLOYEE,
  PATCH_RESET_EMPLOYEE_PASSWORD,
  POST_CREATE_EMPLOYEE,
} from "../../helpers/url_helpers";

const initialState = {
  loading: false,
  employeeListError: "",
  newEmployeeError: "",
  employeeList: [],
  createEmployeeLoading: false,
  domainsList: [],
};

const userManagement = createSlice({
  name: "userManagement",
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload.loading;
    },
    setEmployeeList: (state, action) => {
      state.employeeList = action.payload.employeeList;
    },
    setListingError: (state, action) => {
      state.employeeListError = action.payload.employeeListError;
    },
    setNewEmployeeError: (state, action) => {
      state.newEmployeeError = action.payload.newEmployeeError;
    },
    setCreateEmployeeLoading: (state, action) => {
      state.createEmployeeLoading = action.payload;
    },
    setDomainsList: (state, action) => {
      state.domainsList = action.payload.domainsList;
    },
    setUserActive: (state, action) => {
      state.employeeList.employee_data = state.employeeList.employee_data.map(
        (emp) =>
          emp?.email === action.payload.email
            ? { ...emp, is_active: action.payload.is_active }
            : emp
      );
    },

    reset: (state, action) => {
      if (action.payload?.isListWithSearch) state.employeeList = [];
      state.loading = false;
      state.employeeListError = "";
      state.newEmployeeError = "";
      state.createEmployeeLoading = false;
    },
  },
});

export const {
  setLoading,
  setEmployeeList,
  setListingError,
  setNewEmployeeError,
  reset,
  setCreateEmployeeLoading,
  setDomainsList,
  setUserActive,
} = userManagement.actions;
export default userManagement.reducer;

export const getEmployeeList =
  (searchItem = "", pageNumber = 1, pageSize = "", orgId = 0, surveyId = "") =>
  async (dispatch) => {
    let response;
    try {
      dispatch(setListingError({ employeeListError: "" }));
      dispatch(setLoading({ loading: true }));
      // api call
      response = await get(
        GET_EMPLOYEE_LIST_FOR_PLATFORM_ADMIN.replace("{searchItem}", searchItem)
          .replace("{pageNumber}", pageNumber)
          .replace("{pageSize}", pageSize)
          .replace("{orgId}", orgId)
          .replace("{surveyId}", surveyId)
      );

      if (response) {
        if (response.error) {
          dispatch(
            setListingError({
              employeeListError: response?.error,
            })
          );
        } else {
          dispatch(
            setEmployeeList({
              employeeList: response?.data,
            })
          );
        }
      }
      dispatch(setLoading({ loading: false }));
    } catch (error) {
      dispatch(
        setListingError({
          employeeListError: error,
        })
      );
      dispatch(setLoading({ loading: false }));
    }
  };

export const activateEmployee = (payload, callback) => async (dispatch) => {
  let response;
  try {
    dispatch(setListingError({ employeeListError: "" }));

    // api call
    response = await patch(
      PATCH_ACTIVATE_EMPLOYEE.replace("{orgId}", payload.orgId),
      payload
    );

    if (response) {
      if (response.error) {
        dispatch(
          setListingError({
            employeeListError: response?.error,
          })
        );
      } else {
        dispatch(setUserActive(payload));
      }
    }
  } catch (error) {
    dispatch(
      setListingError({
        employeeListError: error,
      })
    );
  }
};

export const createEmployee = (payload, callback) => async (dispatch) => {
  let response;
  try {
    dispatch(setCreateEmployeeLoading(true));
    dispatch(setNewEmployeeError({ newEmployeeError: "" }));

    // api call
    response = await post(POST_CREATE_EMPLOYEE, payload);

    if (response) {
      dispatch(setCreateEmployeeLoading(false));
      if (response.error) {
        dispatch(
          setNewEmployeeError({
            newEmployeeError: response?.error,
          })
        );
        if (callback) callback("failure");
      } else {
        if (callback) callback("success");
      }
    }
  } catch (error) {
    dispatch(setCreateEmployeeLoading(false));
    dispatch(
      setNewEmployeeError({
        newEmployeeError: error,
      })
    );
  }
};

export const editEmployee = (payload, callback) => async (dispatch) => {
  let response;
  try {
    dispatch(setNewEmployeeError({ newEmployeeError: "" }));

    // api call
    response = await patch(POST_CREATE_EMPLOYEE, payload);

    if (response) {
      if (response.error) {
        dispatch(
          setNewEmployeeError({
            newEmployeeError: response?.error,
          })
        );
        if (callback) callback("failure");
      } else {
        if (callback) callback("success");
      }
    }
  } catch (error) {
    dispatch(
      setNewEmployeeError({
        newEmployeeError: error,
      })
    );
  }
};

export const deleteEmployee = (payload, callback) => async (dispatch) => {
  let response;
  try {
    dispatch(setListingError({ employeeListError: "" }));
    // api call
    response = await patch(
      PATCH_DELETE_EMPLOYEE.replace("{orgId}", payload.orgId),
      payload
    );

    if (response) {
      if (response.error) {
        dispatch(
          setListingError({
            employeeListError: response?.error,
          })
        );
      } else if (callback) callback(response?.message);
    }
  } catch (error) {
    dispatch(
      setListingError({
        employeeListError: error,
      })
    );
  }
};

export const clearNewEmployeeError = () => async (dispatch) => {
  dispatch(setNewEmployeeError({ newEmployeeError: "" }));
};

export const resetEmployeePassword =
  (payload, callback) => async (dispatch) => {
    let response;
    try {
      dispatch(setListingError({ employeeListError: "" }));
      // api call
      response = await patch(PATCH_RESET_EMPLOYEE_PASSWORD, payload);

      if (response) {
        if (response.error) {
          dispatch(
            setListingError({
              employeeListError: response?.error,
            })
          );
        } else if (callback) callback(response?.message);
      }
    } catch (error) {
      dispatch(
        setListingError({
          employeeListError: error,
        })
      );
    }
  };

export const fetchDomains = (surveyId) => async (dispatch) => {
  let response;
  try {
    dispatch(setNewEmployeeError({ newEmployeeError: "" }));
    // api call
    response = await get(GET_DOMAINS_LIST + surveyId);

    if (response) {
      if (response.error) {
        dispatch(
          setNewEmployeeError({
            newEmployeeError: response?.error,
          })
        );
      } else {
        dispatch(
          setDomainsList({
            domainsList: response?.data,
          })
        );
      }
    }
  } catch (error) {
    dispatch(
      setNewEmployeeError({
        newEmployeeError: error,
      })
    );
  }
};
